import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, Plus } from 'lucide-react';
import {
  useBreakdownTemplates,
  useDeleteBreakdownTemplate,
  useSetDefaultTemplate,
  useUpdateBreakdownTemplate,
} from '@/hooks/useBreakdownTemplates';
import { TemplateSidebar } from './components/TemplateSidebar';
import { TemplatePreview } from './components/TemplatePreview';
import { NewTemplateDialog } from './components/NewTemplateDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export default function BreakdownTemplates() {
  const navigate = useNavigate();
  const { data: templates = [], isLoading, isError } = useBreakdownTemplates();
  const deleteM = useDeleteBreakdownTemplate();
  const setDefaultM = useSetDefaultTemplate();
  const updateM = useUpdateBreakdownTemplate();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const handleEditContents = useCallback(
    (id: string) => navigate(`/settings/job-type-templates/${id}/edit`),
    [navigate]
  );

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Delete this template? This cannot be undone.')) return;
      await deleteM.mutateAsync(id);
      if (selectedId === id) setSelectedId(null);
    },
    [deleteM, selectedId]
  );

  const handleRenameOpen = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setRenameId(id);
    setRenameValue(t.name);
    setRenameError(null);
  };

  const handleRenameSubmit = async () => {
    if (!renameId) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameError('Name is required');
      return;
    }
    try {
      await updateM.mutateAsync({ id: renameId, data: { name: trimmed } });
      setRenameId(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setRenameError(msg);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center justify-center h-96 text-red-500">
        Failed to load templates.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-gray-700" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Breakdown Templates</h2>
            <p className="text-sm text-gray-500 mt-1">
              Templates are created from real job breakdowns. The default template for each
              project type is auto-applied to new jobs.
            </p>
          </div>
        </div>
        <Button onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          New template
        </Button>
      </div>

      <div className="flex gap-6 min-h-[600px]">
        <TemplateSidebar
          templates={templates}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDelete={handleDelete}
          onRename={handleRenameOpen}
          onSetDefault={(id) => setDefaultM.mutate(id)}
          onEditContents={handleEditContents}
        />
        <TemplatePreview template={selected} onEditContents={handleEditContents} />
      </div>

      <NewTemplateDialog open={newOpen} onOpenChange={setNewOpen} />

      <Dialog open={!!renameId} onOpenChange={(open) => !open && setRenameId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename template</DialogTitle>
            <DialogDescription>Enter a new name for this template.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={renameValue}
              onChange={(e) => {
                setRenameValue(e.target.value);
                if (renameError) setRenameError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
              }}
            />
            {renameError && <p className="text-xs text-red-600 mt-1">{renameError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameId(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleRenameSubmit}
              disabled={!renameValue.trim() || updateM.isPending}
            >
              {updateM.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
